import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  ButtonGroup,
  Button,
  LinearProgress,
  Divider,
} from '@material-ui/core';
import {
  Timeline,
  AccessTime,
  Payment,
  DateRange,
} from '@material-ui/icons';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { formattedCurrency } from '../../../utils/utils';

const PIE_COLORS = [
  '#2563eb',
  '#10b981',
  '#8b5cf6',
  '#f59e0b',
  '#ec4899',
  '#06b6d4',
  '#64748b',
];

const OverviewSalesCharts = ({ charts, isSingleDay }) => {
  const [chartTab, setChartTab] = useState('timeline'); // 'timeline' | 'shifts' | 'weekdays'

  if (!charts) return null;

  const {
    timeline = [],
    byShift = [],
    byWeekday = [],
    paymentMethods = [],
  } = charts;

  const hasTimelineSales = timeline.some((t) => t.sales > 0 || t.received > 0);

  return (
    <Grid container spacing={3} style={{ marginBottom: 24 }}>
      {/* Coluna Esquerda: Ritmo de Vendas e Comportamento */}
      <Grid item xs={12} md={8}>
        <Paper
          elevation={0}
          style={{
            padding: 20,
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            height: '100%',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <Timeline style={{ color: '#2563eb', marginRight: 8 }} />
              <Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}
                >
                  {isSingleDay
                    ? 'Ritmo de Vendas por Hora'
                    : 'Evolução Diária de Vendas vs. Recebimentos'}
                </Typography>
                <Typography variant="caption" style={{ color: '#64748b' }}>
                  {isSingleDay
                    ? 'Acompanhe os horários de maior movimento e entrada de caixa no dia'
                    : 'Comparativo diário entre vendas realizadas e dinheiro recebido no caixa'}
                </Typography>
              </Box>
            </Box>

            <ButtonGroup size="small" style={{ marginTop: 4 }}>
              <Button
                variant={chartTab === 'timeline' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setChartTab('timeline')}
              >
                {isSingleDay ? 'Por Hora' : 'Por Dia'}
              </Button>
              <Button
                variant={chartTab === 'shifts' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setChartTab('shifts')}
              >
                Turnos
              </Button>
              {!isSingleDay && (
                <Button
                  variant={chartTab === 'weekdays' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setChartTab('weekdays')}
                >
                  Dia da Semana
                </Button>
              )}
            </ButtonGroup>
          </Box>

          <Divider style={{ marginBottom: 20 }} />

          {chartTab === 'timeline' && (
            <Box style={{ width: '100%', height: 300 }}>
              {hasTimelineSales ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={timeline}
                    margin={{ top: 10, right: 15, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(v) => `R$${v}`}
                    />
                    <RechartsTooltip
                      formatter={(val, name) => [
                        formattedCurrency(val),
                        name,
                      ]}
                      labelFormatter={(label) => `Período: ${label}`}
                    />
                    <Legend />
                    <Bar
                      dataKey="sales"
                      name="Vendas Realizadas"
                      fill="#2563eb"
                      radius={[4, 4, 0, 0]}
                      barSize={isSingleDay ? 22 : 28}
                    />
                    <Line
                      type="monotone"
                      dataKey="received"
                      name="Recebido no Caixa"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{ color: '#94a3b8' }}
                >
                  <AccessTime style={{ fontSize: 40, marginBottom: 8, opacity: 0.5 }} />
                  <Typography variant="body2">
                    Nenhuma movimentação registrada neste período.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {chartTab === 'shifts' && (
            <Box py={1}>
              <Grid container spacing={2}>
                {byShift.map((sh) => (
                  <Grid item xs={12} sm={4} key={sh.shift}>
                    <Box
                      p={2}
                      style={{
                        borderRadius: 8,
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, color: '#0f172a' }}
                        >
                          {sh.shift}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ color: '#64748b' }}
                        >
                          {sh.periodLabel}
                        </Typography>
                      </Box>

                      <Typography
                        variant="h5"
                        style={{
                          fontWeight: 800,
                          color: '#2563eb',
                          marginTop: 8,
                          marginBottom: 4,
                        }}
                      >
                        {formattedCurrency(sh.value)}
                      </Typography>

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="caption" style={{ color: '#64748b' }}>
                          {sh.count} {sh.count === 1 ? 'venda' : 'vendas'}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ fontWeight: 700, color: '#0f172a' }}
                        >
                          {sh.pct}% do total
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, sh.pct || 0)}
                        style={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#e2e8f0',
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {chartTab === 'weekdays' && (
            <Box style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={byWeekday}
                  margin={{ top: 10, right: 15, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="weekday" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickFormatter={(v) => `R$${v}`}
                  />
                  <RechartsTooltip
                    formatter={(val) => [formattedCurrency(val), 'Vendas']}
                  />
                  <Bar
                    dataKey="value"
                    name="Vendas por Dia da Semana"
                    fill="#7c3aed"
                    radius={[4, 4, 0, 0]}
                    barSize={32}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Coluna Direita: Meios de Pagamento */}
      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          style={{
            padding: 20,
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Payment style={{ color: '#059669', marginRight: 8 }} />
            <Box>
              <Typography
                variant="h6"
                style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}
              >
                Meios de Pagamento
              </Typography>
              <Typography variant="caption" style={{ color: '#64748b' }}>
                Distribuição do valor recebido no período
              </Typography>
            </Box>
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          {paymentMethods.length > 0 ? (
            <>
              <Box style={{ width: '100%', height: 150, marginBottom: 12 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethods}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={62}
                      paddingAngle={3}
                    >
                      {paymentMethods.map((entry, idx) => (
                        <Cell
                          key={`cell-${entry.id || idx}`}
                          fill={PIE_COLORS[idx % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(val) => formattedCurrency(val)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Box flex={1} style={{ overflowY: 'auto' }}>
                {paymentMethods.map((pm, idx) => {
                  const color = PIE_COLORS[idx % PIE_COLORS.length];
                  return (
                    <Box key={pm.id || idx} mb={1.5}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <Box display="flex" alignItems="center">
                          <Box
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: color,
                              marginRight: 8,
                            }}
                          />
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600, color: '#1e293b' }}
                          >
                            {pm.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ color: '#94a3b8', marginLeft: 6 }}
                          >
                            ({pm.count}x)
                          </Typography>
                        </Box>

                        <Box textAlign="right">
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 700, color: '#0f172a' }}
                          >
                            {formattedCurrency(pm.value)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center">
                        <Box flex={1} mr={1}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, pm.pct || 0)}
                            style={{
                              height: 5,
                              borderRadius: 3,
                              backgroundColor: '#f1f5f9',
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          style={{
                            fontWeight: 600,
                            color: '#64748b',
                            minWidth: 38,
                            textAlign: 'right',
                          }}
                        >
                          {pm.pct}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ color: '#94a3b8', minHeight: 200 }}
            >
              <DateRange style={{ fontSize: 36, marginBottom: 8, opacity: 0.5 }} />
              <Typography variant="body2">
                Nenhum recebimento registrado no período.
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default OverviewSalesCharts;

